import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, Cake, ShoppingCart, Users, Plus, Pencil, Trash2, TrendingUp, IndianRupee, X, LogOut, Lock, BellRing, Star, MessageSquare, CalendarX, Check, EyeOff, Inbox, Newspaper, Upload, Loader2 } from "lucide-react";
import { CATEGORIES, FLAVORS, WEIGHTS } from "../mock";
import { adminLogin, fetchProducts, fetchOrders, fetchStats, adminCreateProduct, adminUpdateProduct, adminDeleteProduct, updateOrderStatus, adminListReviews, adminUpdateReview, adminDeleteReview, fetchBlockedDates, adminAddBlockedDate, adminDeleteBlockedDate, adminListEnquiries, adminListBlog, adminCreateBlog, adminUpdateBlog, adminDeleteBlog, uploadCustomCakeImage } from "../lib/api";
import { inr } from "../lib/format";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ["Received", "Baking", "Out for delivery", "Delivered", "Cancelled"];

const AdminLoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await adminLogin(email, password);
      sessionStorage.setItem("gt_admin_token", res.token);
      onLogin();
    } catch (err) {
      setError("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-12">
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-3xl bakery-shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-[#6B4226] grid place-items-center text-white mx-auto mb-4"><Lock size={24} /></div>
          <h1 className="font-display text-2xl font-bold text-[#3A2417]">Admin Login</h1>
          <p className="text-sm text-[#B07A5C] mt-1">Globaltreats store management</p>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-[#6B4226]">Email</label>
            <input type="email" data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full mt-1.5 h-11 px-4 rounded-xl border border-[#EADBCB] outline-none focus:border-[#D46A8A]" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#6B4226]">Password</label>
            <input type="password" data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full mt-1.5 h-11 px-4 rounded-xl border border-[#EADBCB] outline-none focus:border-[#D46A8A]" />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button type="submit" data-testid="admin-login-btn" disabled={loading} className="w-full h-12 rounded-full bg-[#D46A8A] text-white font-medium hover:bg-[#c25a7b] transition-colors disabled:opacity-60">{loading ? "Signing in..." : "Sign In"}</button>
        </div>
        <p className="text-xs text-[#B07A5C] text-center mt-6">Only authorised Globaltreats admins can access this area.</p>
      </form>
    </div>
  );
};

const Admin = () => {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("gt_admin_token"));
  const [tab, setTab] = useState("dashboard");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ revenue: 0, orders: 0, products: 0, customers: 0 });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const emptyProduct = { name: "", price: "", flavor: FLAVORS[0], weight: WEIGHTS[1], category: "birthday", image: "https://images.unsplash.com/photo-1621303837174-89787a7d4729?w=600&q=80", description: "" };
  const [draft, setDraft] = useState(emptyProduct);
  const [imgUploading, setImgUploading] = useState(false);
  const [newAlert, setNewAlert] = useState(null); // {count, id}
  const [reviews, setReviews] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [posts, setPosts] = useState([]);
  const emptyPost = { title: "", slug: "", excerpt: "", cover_image: "", author: "Swati, Globaltreats", content: "", published: true };
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [blogDraft, setBlogDraft] = useState(emptyPost);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlockedDate, setNewBlockedDate] = useState("");
  const knownIds = useRef(null);

  const beep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = 880;
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      o.start(); o.stop(ctx.currentTime + 0.5);
    } catch (e) { /* ignore */ }
  };

  const loadAll = () => {
    fetchProducts().then(setProducts).catch((e) => console.error("Failed to load products:", e));
    fetchOrders().then((data) => {
      // detect newly arrived orders (compare against known ids)
      const ids = new Set(data.map((o) => o.id));
      if (knownIds.current === null) {
        knownIds.current = ids;
      } else {
        const fresh = data.filter((o) => !knownIds.current.has(o.id));
        if (fresh.length > 0) {
          knownIds.current = ids;
          setNewAlert({ count: fresh.length, name: fresh[0].name, id: fresh[0].id });
          beep();
          setTimeout(() => setNewAlert(null), 12000);
        }
      }
      setOrders(data);
    }).catch((e) => { if (e?.response?.status === 401) logout(); });
    fetchStats().then(setStats).catch(() => {});
    adminListReviews().then(setReviews).catch(() => {});
    adminListEnquiries().then(setEnquiries).catch(() => {});
    adminListBlog().then(setPosts).catch(() => {});
    fetchBlockedDates().then(setBlockedDates).catch(() => {});
  };

  useEffect(() => {
    if (!authed) return;
    loadAll();
    const t = setInterval(loadAll, 15000); // auto-check for new orders every 15s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const logout = () => {
    sessionStorage.removeItem("gt_admin_token");
    setAuthed(false);
  };

  if (!authed) return <AdminLoginForm onLogin={() => setAuthed(true)} />;

  const openAdd = () => { setEditing(null); setDraft(emptyProduct); setShowForm(true); };
  const openEdit = (p) => { setEditing(p.id); setDraft({ name: p.name, price: p.price, flavor: p.flavor, weight: p.weight, category: p.categories[0], image: p.image, description: p.description || "" }); setShowForm(true); };
  const del = async (id) => {
    try {
      await adminDeleteProduct(id);
      loadAll();
    } catch (e) {
      console.error("Failed to delete product:", e);
      alert("Could not delete the product. Please try again.");
    }
  };
  const handleProductImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await uploadCustomCakeImage(fd);
      setDraft((d) => ({ ...d, image: `${BACKEND}${res.url}` }));
    } catch (err) {
      alert(err.response?.data?.detail || "Could not upload the image. Please try again.");
    } finally {
      setImgUploading(false);
    }
  };
  const save = async (e) => {
    e.preventDefault();
    const payload = { name: draft.name, price: Number(draft.price), flavor: draft.flavor, weight: draft.weight, category: draft.category, image: draft.image, description: draft.description };
    try {
      if (editing) await adminUpdateProduct(editing, payload);
      else await adminCreateProduct(payload);
      setShowForm(false);
      loadAll();
    } catch (err) {
      console.error("Failed to save product:", err);
      alert("Could not save the product. Please check the details and try again.");
    }
  };
  const changeStatus = async (oid, status) => {
    await updateOrderStatus(oid, status);
    loadAll();
  };

  const moderateReview = async (id, status) => {
    try {
      await adminUpdateReview(id, status);
      setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (e) { alert("Could not update the review."); }
  };
  const removeReview = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await adminDeleteReview(id);
      setReviews((prev) => prev.filter((r) => r.id !== id));
    } catch (e) { alert("Could not delete the review."); }
  };
  const addHoliday = async () => {
    if (!newBlockedDate) return;
    try {
      const res = await adminAddBlockedDate(newBlockedDate);
      setBlockedDates(res);
      setNewBlockedDate("");
    } catch (e) { alert("Could not add the date."); }
  };
  const removeHoliday = async (date) => {
    try {
      const res = await adminDeleteBlockedDate(date);
      setBlockedDates(res);
    } catch (e) { alert("Could not remove the date."); }
  };
  const pendingReviews = reviews.filter((r) => r.status === "pending").length;

  const openAddBlog = () => { setEditingBlog(null); setBlogDraft(emptyPost); setShowBlogForm(true); };
  const openEditBlog = (p) => { setEditingBlog(p.id); setBlogDraft({ title: p.title, slug: p.slug, excerpt: p.excerpt || "", cover_image: p.cover_image || "", author: p.author || "", content: p.content || "", published: p.published }); setShowBlogForm(true); };
  const saveBlog = async (e) => {
    e.preventDefault();
    const payload = { ...blogDraft };
    try {
      if (editingBlog) await adminUpdateBlog(editingBlog, payload);
      else await adminCreateBlog(payload);
      setShowBlogForm(false);
      adminListBlog().then(setPosts).catch(() => {});
    } catch (err) { alert(err.response?.data?.detail || "Could not save the article."); }
  };
  const togglePublish = async (p) => {
    try {
      await adminUpdateBlog(p.id, { title: p.title, slug: p.slug, excerpt: p.excerpt, cover_image: p.cover_image, author: p.author, content: p.content, published: !p.published });
      setPosts((prev) => prev.map((x) => (x.id === p.id ? { ...x, published: !x.published } : x)));
    } catch (e) { alert("Could not update the article."); }
  };
  const removeBlog = async (id) => {
    if (!window.confirm("Delete this article permanently?")) return;
    try { await adminDeleteBlog(id); setPosts((prev) => prev.filter((x) => x.id !== id)); }
    catch (e) { alert("Could not delete the article."); }
  };

  const statCards = [
    { icon: IndianRupee, label: "Total Revenue", value: inr(stats.revenue), color: "#D46A8A" },
    { icon: ShoppingCart, label: "Orders", value: stats.orders, color: "#6B4226" },
    { icon: Cake, label: "Products", value: stats.products, color: "#7a9a5b" },
    { icon: Users, label: "Customers", value: stats.customers, color: "#C08552" },
  ];

  const tabs = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "products", label: "Products", icon: Cake },
    { key: "orders", label: "Orders", icon: ShoppingCart },
    { key: "reviews", label: "Reviews", icon: MessageSquare },
    { key: "enquiries", label: "Custom Cakes", icon: Inbox },
    { key: "blog", label: "Blog", icon: Newspaper },
    { key: "holidays", label: "Holidays", icon: CalendarX },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#3A2417]">Admin Panel</h1>
          <p className="text-sm text-[#B07A5C]">Manage Globaltreats storefront</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" className="text-sm text-[#D46A8A] hover:underline">View Store →</Link>
          <button onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-[#6B4226] bg-white border border-[#EADBCB] rounded-full px-4 h-10 hover:border-[#D46A8A]"><LogOut size={15} /> Logout</button>
        </div>
      </div>

      {newAlert && (
        <div className="mb-6 flex items-center gap-3 bg-[#E7F3E0] border border-[#9CCB7C] text-[#3f6b2e] rounded-2xl px-5 py-4 bakery-shadow" onClick={() => { setTab("orders"); setNewAlert(null); }} role="button">
          <span className="w-10 h-10 rounded-full bg-[#6BA84F] text-white grid place-items-center animate-pulse"><BellRing size={20} /></span>
          <div className="flex-1">
            <div className="font-semibold">{newAlert.count} new order{newAlert.count > 1 ? "s" : ""} received!</div>
            <div className="text-sm">Latest: {newAlert.name} ({newAlert.id}) — click to view</div>
          </div>
          <button onClick={(e) => { e.stopPropagation(); setNewAlert(null); }} className="w-8 h-8 grid place-items-center rounded-full hover:bg-white/50"><X size={18} /></button>
        </div>
      )}

      <div className="flex gap-2 mb-8 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)} className={`inline-flex items-center gap-2 px-5 h-11 rounded-full text-sm font-medium transition-colors ${tab === t.key ? "bg-[#6B4226] text-white" : "bg-white text-[#6B4226] border border-[#EADBCB] hover:border-[#D46A8A]"}`}>
            <t.icon size={16} /> {t.label}
            {t.key === "orders" && orders.length > 0 && <span className="ml-1 min-w-[20px] h-5 px-1 rounded-full bg-[#D46A8A] text-white text-[11px] grid place-items-center">{orders.length}</span>}
            {t.key === "reviews" && pendingReviews > 0 && <span className="ml-1 min-w-[20px] h-5 px-1 rounded-full bg-[#E8A020] text-white text-[11px] grid place-items-center">{pendingReviews}</span>}
            {t.key === "enquiries" && enquiries.length > 0 && <span className="ml-1 min-w-[20px] h-5 px-1 rounded-full bg-[#D46A8A] text-white text-[11px] grid place-items-center">{enquiries.length}</span>}
          </button>
        ))}
      </div>

      {tab === "dashboard" && (
        <div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {statCards.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 bakery-shadow">
                <div className="w-11 h-11 rounded-full grid place-items-center mb-3" style={{ backgroundColor: `${s.color}1a` }}><s.icon size={22} style={{ color: s.color }} /></div>
                <div className="font-display text-2xl font-bold text-[#3A2417]">{s.value}</div>
                <div className="text-sm text-[#B07A5C]">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl p-6 bakery-shadow">
            <h3 className="font-display text-lg font-semibold text-[#3A2417] mb-4 flex items-center gap-2"><TrendingUp size={20} className="text-[#D46A8A]" /> Recent Orders</h3>
            {orders.length === 0 ? (
              <p className="text-sm text-[#B07A5C] py-6 text-center">No orders yet. New customer orders will appear here instantly.</p>
            ) : (
              <div className="space-y-3">
                {orders.slice(0, 8).map((o) => (
                  <div key={o.id} className="flex items-center justify-between py-2 border-b border-[#F0E4D6] last:border-0">
                    <div><div className="font-medium text-[#3A2417] text-sm">{o.id}</div><div className="text-xs text-[#B07A5C]">{o.name} · {o.phone}</div></div>
                    <div className="text-sm text-[#6B4226] hidden md:block">{o.items?.length} items</div>
                    <div className="font-medium text-[#3A2417]">{inr(o.total)}</div>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-[#FCEFE3] text-[#D46A8A]">{o.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "products" && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={openAdd} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#D46A8A] text-white text-sm font-medium hover:bg-[#c25a7b] transition-colors"><Plus size={18} /> Add Product</button>
          </div>
          <div className="bg-white rounded-2xl bakery-shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FCEFE3] text-[#6B4226]">
                  <tr>
                    <th className="text-left px-5 py-3 font-medium">Product</th>
                    <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Flavour</th>
                    <th className="text-left px-5 py-3 font-medium">Price</th>
                    <th className="text-right px-5 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b border-[#F0E4D6] last:border-0">
                      <td className="px-5 py-3"><div className="flex items-center gap-3"><img src={p.image} alt="" className="w-11 h-11 rounded-lg object-cover" /><span className="font-medium text-[#3A2417]">{p.name}</span></div></td>
                      <td className="px-5 py-3 text-[#6B4226] hidden md:table-cell">{p.flavor} · {p.weight}</td>
                      <td className="px-5 py-3 text-[#3A2417] font-medium">{inr(p.price)}</td>
                      <td className="px-5 py-3"><div className="flex justify-end gap-2">
                        <button onClick={() => openEdit(p)} className="w-9 h-9 grid place-items-center rounded-lg bg-[#FCEFE3] text-[#6B4226] hover:bg-[#F6E7DA]"><Pencil size={16} /></button>
                        <button onClick={() => del(p.id)} className="w-9 h-9 grid place-items-center rounded-lg bg-[#FBEAEA] text-[#D46A8A] hover:bg-[#f7dede]"><Trash2 size={16} /></button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="bg-white rounded-2xl bakery-shadow overflow-hidden">
          {orders.length === 0 ? (
            <p className="text-sm text-[#B07A5C] py-16 text-center">No orders yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FCEFE3] text-[#6B4226]"><tr>
                  <th className="text-left px-5 py-3 font-medium">Order ID</th>
                  <th className="text-left px-5 py-3 font-medium">Customer</th>
                  <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Delivery</th>
                  <th className="text-left px-5 py-3 font-medium">Total</th>
                  <th className="text-left px-5 py-3 font-medium">Status</th>
                </tr></thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-[#F0E4D6] last:border-0">
                      <td className="px-5 py-3 font-medium text-[#3A2417]">{o.id}</td>
                      <td className="px-5 py-3 text-[#6B4226]">{o.name}<div className="text-xs text-[#B07A5C]">{o.phone} · {o.city}</div>
                        {o.items?.some((it) => it.ref_image) && (
                          <div className="flex gap-1.5 mt-1.5" data-testid={`order-refs-${o.id}`}>
                            {o.items.filter((it) => it.ref_image).map((it, idx) => (
                              <button key={idx} type="button" onClick={() => setLightbox(it.ref_image)} title={`Reference for ${it.name}`} className="w-9 h-9 rounded-lg overflow-hidden border border-[#EADBCB] hover:border-[#D46A8A]" data-testid={`order-ref-thumb-${o.id}-${idx}`}>
                                <img src={it.ref_image} alt="reference" className="w-full h-full object-cover" />
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3 text-[#6B4226] hidden md:table-cell">{o.date}<div className="text-xs text-[#B07A5C]">{o.slot}</div></td>
                      <td className="px-5 py-3 font-medium text-[#3A2417]">{inr(o.total)}</td>
                      <td className="px-5 py-3">
                        <select value={o.status} onChange={(e) => changeStatus(o.id, e.target.value)} className="text-xs px-2.5 py-1.5 rounded-lg bg-[#FCEFE3] text-[#6B4226] border border-[#EADBCB] outline-none">
                          {STATUSES.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "reviews" && (
        <div className="space-y-4" data-testid="admin-reviews">
          {reviews.length === 0 ? (
            <p className="text-sm text-[#B07A5C] py-16 text-center bg-white rounded-2xl bakery-shadow">No reviews yet.</p>
          ) : (
            reviews.map((r) => (
              <div key={r.id} className="bg-white rounded-2xl bakery-shadow p-5" data-testid={`admin-review-${r.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#3A2417]">{r.name}</span>
                    <span className="inline-flex">{[1,2,3,4,5].map((i) => <Star key={i} size={13} className={i <= r.rating ? "fill-[#F5A623] text-[#F5A623]" : "fill-transparent text-[#E0C9A6]"} />)}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${r.status === "approved" ? "bg-[#E7F3E0] text-[#4f8a2f]" : r.status === "hidden" ? "bg-[#EEE] text-[#777]" : "bg-[#FDF0D5] text-[#B8860B]"}`}>{r.status}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.status !== "approved" && (
                      <button onClick={() => moderateReview(r.id, "approved")} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-[#E7F3E0] text-[#4f8a2f] text-sm hover:bg-[#d8ecca]" data-testid={`review-approve-${r.id}`}><Check size={15} /> Approve</button>
                    )}
                    {r.status !== "hidden" && (
                      <button onClick={() => moderateReview(r.id, "hidden")} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-[#FCEFE3] text-[#6B4226] text-sm hover:bg-[#f6e0cf]" data-testid={`review-hide-${r.id}`}><EyeOff size={15} /> Hide</button>
                    )}
                    <button onClick={() => removeReview(r.id)} className="w-9 h-9 grid place-items-center rounded-full bg-[#FBEAEA] text-[#D46A8A] hover:bg-[#f7dede]" data-testid={`review-delete-${r.id}`}><Trash2 size={15} /></button>
                  </div>
                </div>
                <p className="text-sm text-[#6B4226] mt-3">{r.message}</p>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "enquiries" && (
        <div className="space-y-4" data-testid="admin-enquiries">
          {enquiries.length === 0 ? (
            <p className="text-sm text-[#B07A5C] py-16 text-center bg-white rounded-2xl bakery-shadow">No custom cake enquiries yet. Requests from the Contact page will appear here with their sample photos.</p>
          ) : (
            enquiries.map((en) => (
              <div key={en.id} className="bg-white rounded-2xl bakery-shadow p-5" data-testid={`admin-enquiry-${en.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-[#3A2417]">{en.name}</div>
                    <div className="text-xs text-[#B07A5C] mt-0.5">
                      {en.phone ? <a href={`tel:${en.phone}`} className="hover:underline">{en.phone}</a> : "No phone"}
                      {en.email && <> · <a href={`mailto:${en.email}`} className="hover:underline">{en.email}</a></>}
                    </div>
                  </div>
                  {en.created_at && <div className="text-xs text-[#B07A5C]">{new Date(en.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>}
                </div>
                {en.message && <p className="text-sm text-[#6B4226] mt-3 whitespace-pre-line">{en.message}</p>}
                {en.images?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid={`enquiry-images-${en.id}`}>
                    {en.images.map((url, idx) => (
                      <button key={idx} type="button" onClick={() => setLightbox(url)} className="w-24 h-24 rounded-xl overflow-hidden border border-[#EADBCB] hover:border-[#D46A8A] transition-colors" data-testid={`enquiry-image-${en.id}-${idx}`}>
                        <img src={url} alt={`sample ${idx + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {tab === "blog" && (
        <div data-testid="admin-blog">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-[#B07A5C]">Write SEO articles to attract customers searching for cakes online.</p>
            <button onClick={openAddBlog} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#D46A8A] text-white text-sm font-medium hover:bg-[#c25a7b] transition-colors" data-testid="blog-new-btn"><Plus size={18} /> New Article</button>
          </div>
          {posts.length === 0 ? (
            <p className="text-sm text-[#B07A5C] py-16 text-center bg-white rounded-2xl bakery-shadow">No articles yet. Click "New Article" to write your first post.</p>
          ) : (
            <div className="space-y-3">
              {posts.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl bakery-shadow p-4 flex items-center gap-4" data-testid={`admin-blog-${p.slug}`}>
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#F6E7DA] shrink-0">
                    {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#3A2417] truncate">{p.title}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${p.published ? "bg-[#E7F3E0] text-[#4f8a2f]" : "bg-[#EEE] text-[#777]"}`}>{p.published ? "Published" : "Draft"}</span>
                    </div>
                    <div className="text-xs text-[#B07A5C] mt-0.5 truncate">/blog/{p.slug}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => togglePublish(p)} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#FCEFE3] text-[#6B4226] text-sm hover:bg-[#f6e0cf]" data-testid={`blog-toggle-${p.slug}`}>{p.published ? <><EyeOff size={14} /> Unpublish</> : <><Check size={14} /> Publish</>}</button>
                    <button onClick={() => openEditBlog(p)} className="w-9 h-9 grid place-items-center rounded-lg bg-[#FCEFE3] text-[#6B4226] hover:bg-[#F6E7DA]" data-testid={`blog-edit-${p.slug}`}><Pencil size={16} /></button>
                    <button onClick={() => removeBlog(p.id)} className="w-9 h-9 grid place-items-center rounded-lg bg-[#FBEAEA] text-[#D46A8A] hover:bg-[#f7dede]" data-testid={`blog-delete-${p.slug}`}><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "holidays" && (
        <div className="max-w-2xl" data-testid="admin-holidays">
          <div className="bg-white rounded-2xl bakery-shadow p-6">
            <h3 className="font-display text-lg font-semibold text-[#3A2417] flex items-center gap-2"><CalendarX size={20} className="text-[#D46A8A]" /> Blocked Delivery Dates</h3>
            <p className="text-sm text-[#B07A5C] mt-1">Customers won&apos;t be able to schedule delivery on these dates (your off days / holidays).</p>
            <div className="flex flex-wrap items-end gap-3 mt-5">
              <div>
                <label className="text-sm font-medium text-[#6B4226]">Add a date</label>
                <input type="date" value={newBlockedDate} min={new Date().toISOString().split("T")[0]} onChange={(e) => setNewBlockedDate(e.target.value)} className="block mt-1.5 h-11 px-4 rounded-xl border border-[#EADBCB] outline-none focus:border-[#D46A8A]" data-testid="holiday-date-input" />
              </div>
              <button onClick={addHoliday} disabled={!newBlockedDate} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#D46A8A] text-white text-sm font-medium hover:bg-[#c25a7b] disabled:opacity-60" data-testid="holiday-add-btn"><Plus size={16} /> Block Date</button>
            </div>
            <div className="mt-6">
              {blockedDates.length === 0 ? (
                <p className="text-sm text-[#B07A5C]">No blocked dates. All days within the delivery window are available.</p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="holiday-list">
                  {blockedDates.map((d) => (
                    <span key={d} className="inline-flex items-center gap-2 bg-[#FCEFE3] text-[#6B4226] rounded-full pl-4 pr-2 py-1.5 text-sm" data-testid={`holiday-item-${d}`}>
                      {new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                      <button onClick={() => removeHoliday(d)} className="w-6 h-6 grid place-items-center rounded-full hover:bg-white" data-testid={`holiday-remove-${d}`}><X size={14} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-[90] bg-black/40 grid place-items-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={save} className="bg-white rounded-2xl p-6 w-full max-w-md bakery-shadow-lg space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#3A2417]">{editing ? "Edit" : "Add"} Product</h3>
              <button type="button" onClick={() => setShowForm(false)} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[#F6E7DA]"><X size={20} className="text-[#6B4226]" /></button>
            </div>
            <div><label className="text-sm font-medium text-[#6B4226]">Name</label><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required className="w-full mt-1.5 h-11 px-4 rounded-xl border border-[#EADBCB] outline-none focus:border-[#D46A8A]" /></div>
            <div><label className="text-sm font-medium text-[#6B4226]">Price (₹ — MRP, 10% off shown on store)</label><input type="number" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} required className="w-full mt-1.5 h-11 px-4 rounded-xl border border-[#EADBCB] outline-none focus:border-[#D46A8A]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-[#6B4226]">Flavour</label><select value={draft.flavor} onChange={(e) => setDraft({ ...draft, flavor: e.target.value })} className="w-full mt-1.5 h-11 px-3 rounded-xl border border-[#EADBCB] outline-none">{FLAVORS.map((f) => <option key={f}>{f}</option>)}</select></div>
              <div><label className="text-sm font-medium text-[#6B4226]">Weight</label><select value={draft.weight} onChange={(e) => setDraft({ ...draft, weight: e.target.value })} className="w-full mt-1.5 h-11 px-3 rounded-xl border border-[#EADBCB] outline-none">{WEIGHTS.map((w) => <option key={w}>{w}</option>)}</select></div>
            </div>
            <div><label className="text-sm font-medium text-[#6B4226]">Category</label><select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="w-full mt-1.5 h-11 px-3 rounded-xl border border-[#EADBCB] outline-none">{CATEGORIES.slice(1).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}</select></div>
            <div>
              <label className="text-sm font-medium text-[#6B4226]">Cake Image</label>
              <div className="flex items-center gap-3 mt-1.5">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#F6E7DA] border border-[#EADBCB] shrink-0 grid place-items-center">
                  {draft.image ? <img src={draft.image} alt="preview" className="w-full h-full object-cover" /> : <Cake size={20} className="text-[#C9B4A2]" />}
                </div>
                <label className={`inline-flex items-center gap-2 h-11 px-4 rounded-full bg-[#FCEFE3] text-[#6B4226] text-sm cursor-pointer hover:bg-[#f6e0cf] transition-colors ${imgUploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="product-image-upload">
                  {imgUploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {imgUploading ? "Uploading..." : "Upload image"}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleProductImage} data-testid="product-image-input" />
                </label>
              </div>
              <input value={draft.image} onChange={(e) => setDraft({ ...draft, image: e.target.value })} placeholder="…or paste an image URL" className="w-full mt-2 h-11 px-4 rounded-xl border border-[#EADBCB] outline-none focus:border-[#D46A8A] text-sm" data-testid="product-image-url" />
            </div>
            <div><label className="text-sm font-medium text-[#6B4226]">Description</label><textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={3} className="w-full mt-1.5 px-4 py-2 rounded-xl border border-[#EADBCB] outline-none focus:border-[#D46A8A] resize-none" /></div>
            <button type="submit" className="w-full h-11 rounded-full bg-[#D46A8A] text-white font-medium hover:bg-[#c25a7b] transition-colors">{editing ? "Save Changes" : "Add Product"}</button>
          </form>
        </div>
      )}
      {showBlogForm && (
        <div className="fixed inset-0 z-[90] bg-black/40 grid place-items-center p-4" onClick={() => setShowBlogForm(false)}>
          <form onSubmit={saveBlog} className="bg-white rounded-2xl p-6 w-full max-w-2xl bakery-shadow-lg space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="blog-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#3A2417]">{editingBlog ? "Edit" : "New"} Article</h3>
              <button type="button" onClick={() => setShowBlogForm(false)} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[#F6E7DA]"><X size={20} className="text-[#6B4226]" /></button>
            </div>
            <div><label className="text-sm font-medium text-[#6B4226]">Title</label><input value={blogDraft.title} onChange={(e) => setBlogDraft({ ...blogDraft, title: e.target.value })} required className="w-full mt-1.5 h-11 px-4 rounded-xl border border-[#EADBCB] outline-none focus:border-[#D46A8A]" data-testid="blog-title-input" /></div>
            <div><label className="text-sm font-medium text-[#6B4226]">URL slug <span className="text-[#B07A5C] font-normal">(optional — auto-generated from title)</span></label><input value={blogDraft.slug} onChange={(e) => setBlogDraft({ ...blogDraft, slug: e.target.value })} placeholder="best-eggless-cakes-in-noida" className="w-full mt-1.5 h-11 px-4 rounded-xl border border-[#EADBCB] outline-none focus:border-[#D46A8A]" /></div>
            <div><label className="text-sm font-medium text-[#6B4226]">Short excerpt <span className="text-[#B07A5C] font-normal">(shown on cards & Google)</span></label><textarea value={blogDraft.excerpt} onChange={(e) => setBlogDraft({ ...blogDraft, excerpt: e.target.value })} rows={2} className="w-full mt-1.5 px-4 py-2 rounded-xl border border-[#EADBCB] outline-none focus:border-[#D46A8A] resize-none" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-[#6B4226]">Cover image URL</label><input value={blogDraft.cover_image} onChange={(e) => setBlogDraft({ ...blogDraft, cover_image: e.target.value })} className="w-full mt-1.5 h-11 px-4 rounded-xl border border-[#EADBCB] outline-none focus:border-[#D46A8A]" /></div>
              <div><label className="text-sm font-medium text-[#6B4226]">Author</label><input value={blogDraft.author} onChange={(e) => setBlogDraft({ ...blogDraft, author: e.target.value })} className="w-full mt-1.5 h-11 px-4 rounded-xl border border-[#EADBCB] outline-none focus:border-[#D46A8A]" /></div>
            </div>
            <div><label className="text-sm font-medium text-[#6B4226]">Content <span className="text-[#B07A5C] font-normal">(HTML — use &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;&lt;li&gt;)</span></label><textarea value={blogDraft.content} onChange={(e) => setBlogDraft({ ...blogDraft, content: e.target.value })} rows={12} className="w-full mt-1.5 px-4 py-3 rounded-xl border border-[#EADBCB] outline-none focus:border-[#D46A8A] resize-y font-mono text-xs" data-testid="blog-content-input" /></div>
            <label className="flex items-center gap-2 text-sm text-[#6B4226]"><input type="checkbox" checked={blogDraft.published} onChange={(e) => setBlogDraft({ ...blogDraft, published: e.target.checked })} className="w-4 h-4 accent-[#D46A8A]" /> Publish immediately (visible on the website)</label>
            <button type="submit" className="w-full h-11 rounded-full bg-[#D46A8A] text-white font-medium hover:bg-[#c25a7b] transition-colors" data-testid="blog-save-btn">{editingBlog ? "Save Changes" : "Publish Article"}</button>
          </form>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[95] bg-black/80 grid place-items-center p-4" onClick={() => setLightbox(null)} data-testid="admin-lightbox" role="button">
          <button type="button" onClick={() => setLightbox(null)} className="absolute top-5 right-5 w-11 h-11 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/25" data-testid="admin-lightbox-close"><X size={24} /></button>
          <img src={lightbox} alt="preview" className="max-h-[90vh] max-w-[90vw] rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default Admin;
